import { ReactNode, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, theme } from 'antd';
import {
  ProjectOutlined,
  RadarChartOutlined,
  DashboardOutlined,
  LogoutOutlined,
  AppstoreOutlined,
  PictureOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined,
  NodeIndexOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import useAuthStore from '../stores/authStore';
import { useBrandStore } from '../stores/brandStore';
import usePluginStore from '../plugins/plugin-manager';
import { defaultPluginRoutes } from '../plugins/plugin-routes';
import client from '../api/client';
import AiAssistantWidget from '../components/ai/AiAssistantWidget';
import PayDialogHost from '../components/payment/PayDialogHost';

const { Header, Sider, Content } = Layout;

const pluginMenuIcons: Record<string, React.ReactNode> = {
  'rtu-config': <SettingOutlined />,
  'rtu-management': <SettingOutlined />,
};

export default function ProLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { token } = theme.useToken();
  const plugins = usePluginStore((s) => s.plugins);
  // H2：平台品牌（白标 OEM）
  const brand = useBrandStore((s) => s.config);
  const brandName = brand?.name ?? 'DTwince 云边智孪';
  const brandShort = brand?.shortName ?? 'DTwince';
  const brandSub = brandName.startsWith(brandShort) ? brandName.slice(brandShort.length).trim() : brandName;

  const handleLogout = async () => {
    // 通知后端记录登出时间与时长（幂等，失败不阻断）
    try {
      await client.post('/auth/logout');
    } catch {
      /* ignore */
    }
    logout();
    // 退出后强制回登录页且不带 redirect——防止下一个登录用户被带到上一个用户的编辑器 URL（数据串号）
    window.location.href = '/login';
  };

  const enabledPluginIds = useMemo(() => {
    return plugins
      .filter((p) => p.enabled && (p.status === 'installed' || p.status === 'loading'))
      .map((p) => p.id);
  }, [plugins]);

  const menuItems = useMemo(() => {
    const baseItems = [
      { key: '/projects', icon: <ProjectOutlined />, label: '3D 场景' },
      { key: '/dashboards', icon: <DashboardOutlined />, label: '可视化大屏' },
      { key: '/datasets', icon: <RadarChartOutlined />, label: '数据集管理' },
      { key: '/materials', icon: <PictureOutlined />, label: '素材库' },
    { key: '/assets', icon: <FolderOpenOutlined />, label: '我的资产' },
      { key: '/addons', icon: <ThunderboltOutlined />, label: '增值服务' },
      { key: '/licenses', icon: <SafetyCertificateOutlined />, label: '设备授权' },
      { key: '/rules', icon: <ThunderboltOutlined />, label: '规则引擎' },
      { key: '/rule-chains', icon: <NodeIndexOutlined />, label: '规则链' },
      { key: '/plugins', icon: <AppstoreOutlined />, label: '插件市场' },
    ];

    const pluginItems = defaultPluginRoutes
      .filter((route) => enabledPluginIds.includes(route.pluginId))
      .map((route) => ({
        key: route.path.replace('/*', ''),
        icon: pluginMenuIcons[route.pluginId] || <SettingOutlined />,
        label: route.metadata?.title || route.pluginId,
      }));

    if (pluginItems.length > 0) {
      return [
        ...baseItems,
        { type: 'divider' as const },
        ...pluginItems,
      ];
    }

    return baseItems;
  }, [enabledPluginIds]);

  return (
    <Layout style={{ height: '100vh' }}>
      <Sider width={220} theme="dark" collapsedWidth={60}>
        <div
          style={{
            height: 48,
            margin: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: token.colorPrimary,
            borderRadius: 8,
            padding: '0 12px',
          }}
        >
          <img src={brand?.logoUrl ?? '/favicon.ico'} alt="Logo" style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0 }} />
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap' }}>{brandShort}{'  '}<span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7 }}>{brandSub || '云边智孪'}</span></span>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }: { key: string }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: token.colorBgContainer,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <span style={{ marginRight: 16, color: token.colorTextSecondary }}>
            {user?.username || '用户'}
          </span>
          <Button type="text" icon={<UserOutlined />} onClick={() => navigate('/account')}>
            个人中心
          </Button>
          <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>
            退出
          </Button>
        </Header>
        <Content style={{ overflow: 'auto' }}>{children}</Content>
      </Layout>
      {/* P2 E-02：全局 AI 帮助助手浮窗 */}
      <AiAssistantWidget />
      {/* P3 支付线：全局支付弹窗宿主 */}
      <PayDialogHost />
    </Layout>
  );
}
